import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Loader2, BookOpen, Video, Wind, Headphones, PhoneCall, BookMarked, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  article: { icon: BookOpen, label: 'Article', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  video: { icon: Video, label: 'Video', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  breathing: { icon: Wind, label: 'Exercise', bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  podcast: { icon: Headphones, label: 'Podcast', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  helpline: { icon: PhoneCall, label: 'Helpline', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  book: { icon: BookMarked, label: 'Book', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
};

export default function WelfareResources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/welfare/resources`);
        setResources(res.data || []);
      } catch (e) {
        console.error('Failed to load resources:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  if (resources.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-gray-500">No resources yet.</CardContent></Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="welfare-resources-container">
      <p className="text-sm text-gray-500">Hand-picked support for grassroots coaches. All free or freely accessible. If you're in crisis, please contact a helpline or your GP.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {resources.map(r => {
          const meta = TYPE_META[r.type] || TYPE_META.article;
          const Icon = meta.icon;
          return (
            <a
              key={r.id}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`block border ${meta.border} ${meta.bg} rounded-xl p-4 hover:shadow-md transition-all group`}
              data-testid={`welfare-resource-${r.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg ${meta.bg} ${meta.text} flex items-center justify-center flex-shrink-0 border ${meta.border}`}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs uppercase tracking-wider font-medium ${meta.text}`}>{meta.label}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:underline flex items-center gap-1">
                    {r.title}
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </h3>
                  {r.description && <p className="text-sm text-gray-600 mt-1">{r.description}</p>}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
